import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import { WIDGET_ICONS } from "../../../../../../constants";
import useCompareState from "../../../../../../hooks/useCompareState";
import ImageRadio from "../../../../../../parts/inputs/image-radio";
import SelectDays from "../../../../../../parts/inputs/select-days";
import ConfirmationModal from "../../../../../../parts/modals/ConfirmationModal";
import { EditWidgetInfoModal } from "../../../../../../parts/modals/EditWidgetInfo";
import SvgIcon from "../../../../../../parts/SvgIcon";
import scriptLoader from "../../../../../../utils/scriptLoader";
import { isPullActiveCoach } from "../../../../../../utils/user-helpers";
import {
  deleteWidgetUtil,
  orderWidgets,
} from "../../../../../../utils/widgets";
import { getUserToRequest } from "../../../../../dashboard/settings/manage-coaches/utils/manage-coaches-utils";
import { EditDailyAccountability } from "./EditDailyAccountability";
import WidgetManagerHeader from "./WidgetManagerHeader";

const switchTemplateStatus = (id, status) => {
  submit(`/actions/widget-daily-accountability/status/${id}`, {
    method: "POST",
    body: { status },
  });
};

const TemplateToggle = ({ status, id }) => {
  const { t } = useTranslation("coaching-portal");
  const [openedConfirmationModal, setOpenedConfirmationModal] = useState(false);
  const isPublished = status === "published";

  const handleChange = () => {
    switchTemplateStatus(id, isPublished ? "draft" : "published");
    setTimeout(() => {
      setOpenedConfirmationModal(false);
    }, 500);
  };
  return (
    <>
      <input
        type="checkbox"
        id={`switch-${id}`}
        name={`switch-${id}`}
        role="switch"
        onClick={(e) => {
          e.stopPropagation();
        }}
        onChange={(e) => {
          setOpenedConfirmationModal(!openedConfirmationModal);
        }}
        checked={isPublished}
        className="ml-4"
      />
      <ConfirmationModal
        title={
          isPublished
            ? t("widgets.common.unpublish")
            : t("widgets.common.publish")
        }
        contentText={
          isPublished
            ? t("widgets.dailyAccountabilityManager.confirmUnpublishTemplate")
            : t("widgets.dailyAccountabilityManager.confirmPublishTemplate")
        }
        cancelButtonText={t("widgets.common.cancel")}
        cancelButtonAction={(e) => {
          e.stopPropagation();
          setOpenedConfirmationModal(false);
        }}
        confirmButtonText={
          isPublished
            ? t("widgets.common.unpublish")
            : t("widgets.common.publish")
        }
        confirmButtonAction={(e) => {
          e.stopPropagation();
          handleChange();
        }}
        isOpen={openedConfirmationModal}
        icon="delete"
        confirmSuccess={!isPublished}
      />
    </>
  );
};

export const DailyAccountabilityWidgetManager = ({ locals, goBack }) => {
  const { t } = useTranslation("coaching-portal");
  const {
    activeUser,
    activeWidget,
    dailyAccountabilityTemplates,
    organizationOwner,
  } = locals;
  const userToRequest = getUserToRequest(activeUser, organizationOwner);

  const handleDelete = (e, id) => {
    if (
      process.browser &&
      confirm(t("widgets.dailyAccountabilityManager.confirmDeleteTemplate"))
    ) {
      e.stopPropagation();
      e.preventDefault();
      submit(`/actions/widget-daily-accountability/delete/${id}`, {
        method: "GET",
      });
    }
  };

  const [formState, setFormState] = useState({});
  const [dailyAccountabilityState, setDailyAccountabilityState] = useState([]);
  const [addNew, setAddNew] = useState("addNewButton");
  const [statusState, setStatusState] = useState("save");
  const [refreshList, setRefreshList] = useState(false);
  const [editWidgetInfoModalOpened, setEditWidgetInfoModalOpened] =
    useState(false);
  //state to control the accordion state
  const [accordionState, setAccordionState] = useState({
    isOpen: false,
    _id: "",
  });
  const [changedOrder, setChangedOrder] = useState(false);

  useEffect(() => {
    let sortable = null;
    if (document.querySelector(".principal-grabber-div")) {
      scriptLoader(
        "https://cdn.jsdelivr.net/npm/sortablejs@1.15.7/Sortable.min.js",
        () => {
          const dragImage = document.createElement("img");
          dragImage.src =
            "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
          sortable = new Sortable(
            document.querySelector(".principal-grabber-div"),
            {
              animation: 350,
              handle: ".drag-wrapper",
              setData(dataTransfer) {
                dataTransfer.setDragImage(dragImage, 0, 0);
              },
              onEnd: (e) => {
                if (e.oldIndex === e.newIndex) return;

                const items = e.from.querySelectorAll(":scope > div");
                e.from.insertBefore(
                  e.item,
                  items[e.oldIndex + (e.oldIndex > e.newIndex)]
                );

                const ordered = Array.from(items).map((element) => {
                  const listInput = element.querySelector(".list-section-div");
                  const idInput = element.querySelector(".id-section-div");
                  const nameInput = element.querySelector(".name-section-div");
                  const statusInput = element.querySelector(
                    ".status-section-div"
                  );

                  return {
                    widget: activeWidget._id,
                    _id: (idInput && idInput.value) || "",
                    status: (statusInput && statusInput.value) || "",
                    name: (nameInput && nameInput.textContent) || "",
                    list:
                      (listInput?.textContent &&
                        JSON.parse(listInput.textContent)) ||
                      "",
                  };
                });
                setDailyAccountabilityState(ordered);
                setChangedOrder(true);
                return false;
              },
            }
          );
        }
      );
    }
    return () => {
      if (sortable) sortable.destroy();
    };
  }, []);

  const [compareBase, setCompareBase, setCurrentCompareState, hasChanged] =
    useCompareState();
  useEffect(() => {
    if (activeWidget) {
      setFormState({
        id: activeWidget._id,
        name: activeWidget.name,
        description: activeWidget.description,
        icon: activeWidget.icon,
        order: activeWidget.order,
        status: activeWidget.status,
        type: activeWidget.type,
        pushPermission:
          activeWidget.pushPermission === undefined
            ? true
            : activeWidget.pushPermission,

        pullPermission:
          activeWidget.pullPermission === undefined
            ? true
            : activeWidget.pullPermission,
      });
      const comparingBase = {
        name: activeWidget.name,
        description: activeWidget.description,
        icon: activeWidget.icon,
        pushPermission:
          activeWidget.pushPermission === undefined
            ? true
            : activeWidget.pushPermission,
        pullPermission:
          activeWidget.pullPermission === undefined
            ? true
            : activeWidget.pullPermission,
      };
      setCompareBase(comparingBase);
      setCurrentCompareState(comparingBase);
    }
    if (dailyAccountabilityTemplates) {
      setDailyAccountabilityState(dailyAccountabilityTemplates);
    }
  }, [activeWidget, dailyAccountabilityTemplates]);

  useEffect(() => {
    if (locals.query.submited) {
      setAddNew("addNewButton");
    }
  }, [locals.query]);

  const prevTemplates = useRef(dailyAccountabilityTemplates);
  useEffect(() => {
    const previousTemplates = prevTemplates.current;
    const currentTemplates = dailyAccountabilityTemplates;

    if (currentTemplates.length > previousTemplates.length) {
      const newTemplate = currentTemplates.at(-1);
      // Update accordionState to open with the new template
      setAccordionState({
        isOpen: true,
        _id: newTemplate._id,
        name: newTemplate.name,
        list: newTemplate.list,
      });
    }
    // Update the ref with the current templates after comparison
    prevTemplates.current = currentTemplates;
  }, [dailyAccountabilityTemplates]);

  const {
    description = "",
    name = "",
    icon = "",
    id = 0,
    pushPermission = true,
    pullPermission = true,
  } = formState;

  const handleSaveReorder = (e) => {
    e.preventDefault();
    const orderedTemplates = dailyAccountabilityState.map(
      (element) => element._id
    );
    if (orderedTemplates.length > 0) {
      setStatusState("save");
      submit("/actions/widget-daily-accountability/reorder", {
        method: "POST",
        body: { orderedTemplates },
      });
    }
  };
  const cancelReorder = () => {
    setDailyAccountabilityState(dailyAccountabilityTemplates);
    setStatusState("save");
    setRefreshList(!refreshList);
  };

  const validationSubmit = () => {
    return !!(description.length > 0 && name.length > 0 && icon.length > 0);
  };

  const handleInputChange = ({ target }) => {
    const targetName = target?.name.replace("-mobile", "") || target?.name;
    const newState = { ...formState, [targetName]: target.value };

    setFormState(newState);
    setCurrentCompareState({
      ...compareBase,
      [targetName]: target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const body = formState;
    delete body.order;
    delete body.status;
    delete body.type;
    delete body.id;

    submit(`/actions/widgets/save/${id}`, {
      method: "POST",
      body: body,
    });
  };
  const handleGoToMain = () => {
    if (accordionState.isOpen) {
      setAccordionState({ isOpen: false, _id: "" });
    }
    if (addNew === "addNewForm") {
      setAddNew("addNewButton");
    }
  };

  const handleChangeStatusState = (state) => {
    setStatusState(state);
  };

  const deleteWidget = (e) => {
    if (activeWidget) {
      deleteWidgetUtil(e, activeWidget._id);
    }
  };

  const onOpenEditWidgetInfo = () => {
    setEditWidgetInfoModalOpened(!editWidgetInfoModalOpened);
  };

  const WidgetTogglePush = ({ permission }) => (
    <div className="labeled-switch" sz="large">
      <span>{t("widgets.common.subscriptionsPush")}</span>
      <input
        type="checkbox"
        id="switch-1"
        name="switch-1"
        role="switch"
        onChange={() => {
          const newState = {
            ...formState,
            pushPermission: !formState.pushPermission,
          };
          setFormState(newState);
          setCurrentCompareState({
            ...compareBase,
            pushPermission: !formState.pushPermission,
          });
        }}
        checked={permission ? true : undefined}
      />
    </div>
  );

  const WidgetTogglePull = ({ permission }) => (
    <div className="labeled-switch" sz="large">
      <span>{t("widgets.common.coachingPull")}</span>
      <input
        type="checkbox"
        id="switch-1"
        name="switch-1"
        role="switch"
        onChange={() => {
          const newState = {
            ...formState,
            pullPermission: !formState.pullPermission,
          };
          setFormState(newState);
          setCurrentCompareState({
            ...compareBase,
            pullPermission: !formState.pullPermission,
          });
        }}
        checked={permission ? true : undefined}
      />
    </div>
  );

  return (
    <main className="widgets-manager list daily-accountability">
      <WidgetManagerHeader
        name={name}
        description={description}
        goBack={goBack}
        onOpenEditWidgetInfo={onOpenEditWidgetInfo}
      />
      <EditWidgetInfoModal
        widgetName={formState.name}
        widgetDescription={formState.description}
        widgetIcon={formState.icon}
        isOpen={editWidgetInfoModalOpened}
        onClose={onOpenEditWidgetInfo}
        handleInputChange={handleInputChange}
        handleSubmit={handleSubmit}
        validationSubmit={validationSubmit}
        iconsRadios={WIDGET_ICONS["daily-accountability-template"]}
        WidgetTogglePull={WidgetTogglePull}
        WidgetTogglePush={WidgetTogglePush}
        userToRequest={userToRequest}
        pullPermission={pullPermission}
        pushPermission={pushPermission}
      />
      <div>
        {!!activeWidget && (
          <aside className="mobile-hidden">
            <div className="overflow-auto max-h-[calc(100vh-16.75rem)]">
              <label htmlFor="name">{t("widgets.common.widgetName")}</label>
              <input
                name="name"
                className="text-input"
                placeholder={t("widgets.common.enterName")}
                value={name}
                maxLength="30"
                onChange={handleInputChange}
              />
              <label htmlFor="description">
                {t("widgets.dailyAccountabilityManager.widgetDescription")}
              </label>
              <textarea
                maxLength="60"
                name="description"
                className="text-input"
                placeholder={t("editAccountability.descriptionPlaceholder")}
                value={description}
                onChange={handleInputChange}
              />

              <ImageRadio
                label="Widget icon"
                name="icon"
                currentValue={icon}
                radios={WIDGET_ICONS["daily-accountability-template"]}
                onChange={handleInputChange}
                isManager={true}
              />

              <label>{t("editWidgetInfo.userTypeAccess")}</label>
              {isPullActiveCoach(userToRequest) && (
                <WidgetTogglePull permission={pullPermission} />
              )}
              {userToRequest.pushAccountInfo && (
                <WidgetTogglePush permission={pushPermission} />
              )}
            </div>
            <footer>
              <button
                data-button-type="secondary"
                data-action="cancel"
                onClick={deleteWidget}
                type="button"
              >
                {t("widgets.common.delete")}
              </button>
              <button
                data-button-type="primary"
                onClick={handleSubmit}
                disabled={!hasChanged}
                type="button"
              >
                {t("widgets.common.save")}
              </button>
            </footer>
          </aside>
        )}
        {!!dailyAccountabilityTemplates && (
          <section>
            <header>
              <section className="only-show-mobile back-handler">
                <SvgIcon
                  src={"/img/new-icons/back-button.svg"}
                  onClickFn={() =>
                    submit(`/actions/refresh/?`, {
                      method: "GET",
                      body: { section: "", learn: "" },
                    })
                  }
                />
                <h3>{activeWidget?.name || t("widgets.common.explore")}</h3>
              </section>
              <hgroup className="mobile-hidden">
                {(addNew === "addNewForm" || accordionState.isOpen) && (
                  <SvgIcon
                    src="/img/new-icons/back-button.svg"
                    onClickFn={handleGoToMain}
                    classNames="cursor-pointer"
                  />
                )}
                <div>
                  <h4>
                    {addNew === "addNewForm"
                      ? t("widgets.dailyAccountabilityManager.addNewTemplate")
                      : accordionState.isOpen
                        ? accordionState.name
                        : t(
                            "widgets.dailyAccountabilityManager.dailyAccountabilityTemplates"
                          )}
                  </h4>
                </div>
              </hgroup>

              {statusState === "save" &&
                !accordionState.isOpen &&
                addNew === "addNewButton" &&
                dailyAccountabilityState.length > 1 && (
                  <button
                    data-button-type="secondary"
                    onClick={() => handleChangeStatusState("change-order")}
                    type="button"
                  >
                    {t("widgets.common.changeOrder")}
                  </button>
                )}
              {statusState === "change-order" && (
                <hgroup className="actions-wrapper">
                  <button
                    data-button-type="primary"
                    onClick={handleSaveReorder}
                    disabled={!changedOrder}
                    type="button"
                  >
                    {t("widgets.common.save")}
                  </button>
                  <button
                    data-button-type="secondary"
                    data-action="cancel"
                    onClick={cancelReorder}
                    type="button"
                  >
                    {t("widgets.common.cancel")}
                  </button>
                </hgroup>
              )}
            </header>
            {addNew === "addNewButton" && !accordionState.isOpen && (
              <div className="principal-grabber-div">
                {orderWidgets(dailyAccountabilityState).map(
                  ({ _id, name, status, list }) => {
                    return (
                      <div
                        className="article-wrapper daily-accountability"
                        key={`${_id}-${refreshList}`}
                      >
                        <article
                          id={_id}
                          className={
                            statusState === "change-order"
                              ? "columns column item-grabber-container"
                              : "columns item-grabber-container"
                          }
                        >
                          <div style={{ display: "none" }}>
                            <input
                              className="status-section-div"
                              name="status"
                              readOnly
                              value={status}
                            />
                            <input
                              className="id-section-div"
                              name="id"
                              readOnly
                              value={_id}
                            />
                            <span className="list-section-div">
                              {JSON.stringify(list)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center gap-4 grow px-4 pb-4">
                            <p className="s2 name-section-div">{name}</p>
                            <div className="flex items-center gap-2">
                              <button
                                data-button-type="secondary"
                                data-icon-only
                                type="button"
                              >
                                <SvgIcon
                                  src="/img/new-icons/edit-pencil.svg"
                                  onClickFn={() => {
                                    setAccordionState({
                                      _id,
                                      name,
                                      list,
                                      isOpen: true,
                                    });
                                  }}
                                />
                              </button>
                              <button
                                data-button-type="secondary"
                                data-icon-only
                                type="button"
                              >
                                <SvgIcon
                                  src={"/img/new-icons/red-trash.svg"}
                                  onClickFn={(event) =>
                                    handleDelete(event, _id)
                                  }
                                  noFilterOnLight={true}
                                />
                              </button>
                              <TemplateToggle status={status} id={_id} />
                            </div>
                          </div>

                          {list?.length > 0 &&
                            list.map((item) => (
                              <div
                                key={item._id}
                                className="accountability-list"
                              >
                                <p className="p5">{item.title}</p>
                                <SelectDays
                                  days={item.days}
                                  startingCalendarDay={
                                    activeUser?.startingCalendarDay ?? 0
                                  }
                                />
                              </div>
                            ))}
                        </article>
                        {statusState === "change-order" && (
                          <div className="drag-wrapper">
                            <SvgIcon
                              src="/img/new-icons/menu.svg"
                              className="drag-handle mobile-hidden"
                              mobileSrc={"/img/new-icons/drag.svg"}
                            />
                          </div>
                        )}
                      </div>
                    );
                  }
                )}
              </div>
            )}
            {accordionState.isOpen && (
              <EditDailyAccountability
                _id={accordionState._id}
                nameProp={accordionState.name}
                list={accordionState.list}
                widget={activeWidget._id}
                startingCalendarDay={activeUser?.startingCalendarDay ?? 0}
              />
            )}
            {addNew === "addNewButton" && !accordionState.isOpen && (
              <div
                className="dashed-box"
                onClick={() => {
                  setAccordionState({ isOpen: false, _id: "" });
                  setAddNew("addNewForm");
                }}
              >
                <SvgIcon
                  src="/img/new-icons/add-square-outline.svg"
                  alt="back"
                />
                <p className="p4">
                  {t("widgets.dailyAccountabilityManager.addNewTemplate")}
                </p>
              </div>
            )}
            {addNew === "addNewForm" && !accordionState.isOpen && (
              <EditDailyAccountability
                widget={activeWidget._id}
                startingCalendarDay={activeUser?.startingCalendarDay ?? 0}
              />
            )}
            {addNew === "addNewButton" ||
              (addNew === "editForm" && (
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <button
                    className={
                      validationSubmit()
                        ? "save-macro-btn"
                        : "save-macro-disabled-btn save-macro-btn"
                    }
                    onClick={handleSubmit}
                    disabled={!validationSubmit()}
                    type="button"
                  >
                    {t("widgets.common.save")}
                  </button>
                </div>
              ))}
          </section>
        )}
      </div>
    </main>
  );
};
